import Button from "@/components/Button";
import { FormInput, FormTextarea } from "@/components/FormField";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { useState } from "react";

const GOOGLE_FORM_URL =
  "https://docs.google.com/forms/d/e/1FAIpQLSclLu2W28q1pXtJN5w0ajJ_x-AmQ2-esu8h6KbJOc2RFUZWow/formResponse";

const GOOGLE_FORM_ENTRIES = {
  creatorName: "entry.1788226040",
  email: "entry.1992580104",
  mainPlatformLink: "entry.1200838135",
  secondarySocials: "entry.1789872976",
  motivation: "entry.1858109958",
} as const;

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ApplicationForm = () => {
  const { t } = useTranslation(["company", "common"]);
  const [creatorName, setCreatorName] = useState("");
  const [email, setEmail] = useState("");
  const [mainPlatformLink, setMainPlatformLink] = useState("");
  const [secondarySocials, setSecondarySocials] = useState("");
  const [motivation, setMotivation] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [touched, setTouched] = useState({
    creatorName: false,
    email: false,
    mainPlatformLink: false,
    motivation: false,
  });

  const markTouched = (field: keyof typeof touched) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const clearTouched = (field: keyof typeof touched) => {
    setTouched((prev) => ({ ...prev, [field]: false }));
  };

  const validateField = (field: string, value: string): string | undefined => {
    const trimmed = value.trim();
    if (!trimmed) {
      return t("partner.form.errors.required");
    }
    if (field === "email" && !EMAIL_REGEX.test(trimmed)) {
      return t("partner.form.errors.invalidEmail");
    }
    return undefined;
  };

  const errors = {
    creatorName: touched.creatorName
      ? validateField("creatorName", creatorName)
      : undefined,
    email: touched.email ? validateField("email", email) : undefined,
    mainPlatformLink: touched.mainPlatformLink
      ? validateField("mainPlatformLink", mainPlatformLink)
      : undefined,
    motivation: touched.motivation
      ? validateField("motivation", motivation)
      : undefined,
  };

  const isFormValid =
    !validateField("creatorName", creatorName) &&
    !validateField("email", email) &&
    !validateField("mainPlatformLink", mainPlatformLink) &&
    !validateField("motivation", motivation);

  const handleSubmit = async () => {
    // Mark all fields as touched to show any remaining errors
    setTouched({
      creatorName: true,
      email: true,
      mainPlatformLink: true,
      motivation: true,
    });

    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append(GOOGLE_FORM_ENTRIES.creatorName, creatorName);
    formData.append(GOOGLE_FORM_ENTRIES.email, email);
    formData.append(GOOGLE_FORM_ENTRIES.mainPlatformLink, mainPlatformLink);
    formData.append(GOOGLE_FORM_ENTRIES.secondarySocials, secondarySocials);
    formData.append(GOOGLE_FORM_ENTRIES.motivation, motivation);

    try {
      await fetch(GOOGLE_FORM_URL, {
        method: "POST",
        body: formData,
        mode: "no-cors",
      });

      setIsSubmitted(true);
      setCreatorName("");
      setEmail("");
      setMainPlatformLink("");
      setSecondarySocials("");
      setMotivation("");
      setTouched({
        creatorName: false,
        email: false,
        mainPlatformLink: false,
        motivation: false,
      });
    } catch {
      // With no-cors mode, we can't read the response, but the data is still sent
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <article className="px-6 mobile:px-10 pb-10">
        <div className="bg-inner-box border border-panel-foreground rounded-2xl p-4 mobile:p-6 flex flex-col gap-y-6 items-center shadow-alert">
          <div className="flex flex-col gap-y-3 items-center text-center">
            <h3 className="text-base mobile:text-xl font-bold">
              {t("partner.submitted.title")}
            </h3>
            <p className="text-sm mobile:text-base text-dark-secondary">
              <Trans
                ns="company"
                i18nKey="partner.submitted.body"
                components={{ br: <br /> }}
              />
            </p>
          </div>
        </div>
      </article>
    );
  }

  return (
    <article id="application-form" className="px-6 mobile:px-10 pb-10">
      <div className="bg-inner-box border border-panel-foreground rounded-2xl p-4 mobile:p-6 flex flex-col gap-y-8 shadow-alert">
        <div className="flex flex-col gap-y-6 items-center">
          <FormInput
            label={t("partner.form.creatorName.label")}
            placeholder={t("partner.form.creatorName.placeholder")}
            value={creatorName}
            onChange={setCreatorName}
            onBlur={() => markTouched("creatorName")}
            onFocus={() => clearTouched("creatorName")}
            error={errors.creatorName}
          />
          <FormInput
            label={t("common:email")}
            placeholder={t("partner.form.emailPlaceholder")}
            value={email}
            onChange={setEmail}
            onBlur={() => markTouched("email")}
            onFocus={() => clearTouched("email")}
            type="email"
            error={errors.email}
          />
          <FormInput
            label={t("partner.form.mainPlatformLink")}
            placeholder={t("partner.form.insertLink")}
            value={mainPlatformLink}
            onChange={setMainPlatformLink}
            onBlur={() => markTouched("mainPlatformLink")}
            onFocus={() => clearTouched("mainPlatformLink")}
            type="url"
            error={errors.mainPlatformLink}
          />
          <FormInput
            label={t("partner.form.secondarySocials")}
            placeholder={t("partner.form.insertLink")}
            value={secondarySocials}
            onChange={setSecondarySocials}
          />
          <FormTextarea
            label={t("partner.form.motivation.label")}
            placeholder={t("partner.form.motivation.placeholder")}
            value={motivation}
            onChange={setMotivation}
            onBlur={() => markTouched("motivation")}
            onFocus={() => clearTouched("motivation")}
            error={errors.motivation}
          />

          <div className="w-[166px]">
            <Button
              variant="primary"
              label={
                isSubmitting
                  ? t("partner.form.submitting")
                  : t("partner.form.submit")
              }
              onClick={handleSubmit}
              large
              disabled={isSubmitting}
            />
          </div>
        </div>

        <p className="text-sm mobile:text-sm text-dark-tertiary">
          {t("partner.form.reviewNote")}
        </p>
      </div>
    </article>
  );
};

export const BecomePartner = () => {
  const { t } = useTranslation("company");
  return (
    <main className="min-h-screen mx-auto bg-panel-base flex justify-center h-full">
      <div className="max-w-[680px] mt-12 flex flex-col gap-y-10">
        <article className="flex flex-col gap-y-2.5 px-6 mobile:px-10">
          <h1 className="text-lg mobile:text-3xl font-bold">
            {t("partner.title")}
          </h1>
          <h3 className="text-dark-secondary text-sm mobile:text-base">
            <Trans
              ns="company"
              i18nKey="partner.intro"
              components={{ br: <br /> }}
            />
          </h3>
        </article>
        <ApplicationForm />
        <div className="flex justify-center pb-20">
          <img
            className="h-[250px]"
            src={`${import.meta.env.VITE_APP_ASSETS_BASE_URL}/creator-academy/easysbc-world-cup.png`}
            alt={t("partner.trophyAlt")}
          />
        </div>
      </div>
    </main>
  );
};
